import { getConfigForImportedTest, getConfigForMeasuredTest, getConfigForTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  mergeConfig,
  wrapAsDerivedFromMeasurementsConfig as wrap,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import getDerivedFromMeasurementsConfig from 'Components/Worksheets/MaximumDryDensityWorksheet/derivedFromMeasurementsConfig';
import getMeasuredUsingConfig from 'Components/Worksheets/MaximumDryDensityWorksheet/measuredUsingConfig';
import { GradningZoneType, MouldSize } from 'Components/Worksheets/MaximumDryDensityWorksheet/types';
import { default as qestIds } from 'Components/Worksheets/QestIds';
import { FormatTypes } from 'Lib/Format';
import * as oPath from 'Lib/Object/Path/Immutable';
import getLabelConfig from './labelConfig';

const getTestPortionMassReadability = (configArgs: IConfigFunctionArgs, prefix: string) => {
  const gradingZone = oPath.get(configArgs.worksheet, `${prefix}.Preparation.GradingZone`);
  const mouldSize = oPath.get(configArgs.worksheet, `${prefix}.Preparation.MouldType`);

  if (
    (gradingZone === GradningZoneType.Two && mouldSize === MouldSize.OneLitre) ||
    gradingZone === GradningZoneType.Four
  ) {
    return [
      `${prefix}.Preparation.CumulativeMassRetainedOn20mmSieve`,
      `${prefix}.Preparation.CumulativeMassRetainedOn37_5mmSieve`,
      `${prefix}.Preparation.TareMass`,
    ];
  } else {
    return [`${prefix}.Preparation.TareMass`];
  }
};

const getDerivedFromMeasurementsConfigBS = (prefix: string) => ({
  [`${prefix}.Preparation.TotalSampleMass`]: wrap([
    `${prefix}.Preparation.TareMass`,
    `${prefix}.Preparation.TareAndTotalSampleMass`,
  ]),
  [`${prefix}.Preparation.TestPortionMass`]: (configArgs: IConfigFunctionArgs) =>
    wrap(getTestPortionMassReadability(configArgs, prefix)),
});

const getReadabilityBasedMouldTypeValue = (worksheet: any, prefix: string) => {
  const mouldType = oPath.get(worksheet, `${prefix}.Preparation.MouldType`) ?? MouldSize.OneLitre;

  return {
    format: mouldType === MouldSize.OneLitre ? { nearest: 0.1 } : { nearest: 0.5 },
    uom: 'mm',
  };
};

const getConfig = (prefix: string) =>
  mergeConfig(
    {
      [`${prefix}.Preparation.StabiliserAmount`]: { format: { nearest: 0.1 } },
      [`${prefix}.Preparation.TareMass`]: { uom: 'g', format: { nearest: 1 } },
      [`${prefix}.Preparation.TareAndTotalSampleMass`]: { uom: 'g', format: { nearest: 1 } },
      [`${prefix}.Preparation.AssociatedOversizeDetermination.MeasuredTest.Sieves.DryMassPassing`]: {
        uom: 'g',
        format: { nearest: 1 },
      },
      [`${prefix}.Preparation.AssociatedOversizeDetermination.MeasuredTest.TotalMaterialDryMass`]: {
        uom: 'g',
        format: { nearest: 1 },
      },
      [`${prefix}.Preparation.TotalSampleMass`]: { uom: 'g', format: { nearest: 1 } },
      [`${prefix}.Preparation.CumulativeMassRetainedOn20mmSieve`]: { uom: 'g', format: { nearest: 1 } },
      [`${prefix}.Preparation.CumulativeMassRetainedOn37_5mmSieve`]: { uom: 'g', format: { nearest: 1 } },
      [`${prefix}.Preparation.TestPortionMass`]: { uom: 'g', format: { nearest: 1 } },

      [`${prefix}.Equipment.Moulds.Moulds.Mass`]: { uom: 'g', format: { nearest: 0 } },
      [`${prefix}.Equipment.Moulds.Moulds.Volume`]: { uom: 'cm³', format: { nearest: 0.01 } },
      [`${prefix}.Equipment.Moulds.Moulds.HeightReadingsAverage`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.DiameterReadingsAverage`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.HeightReading1`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.HeightReading2`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.HeightReading3`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.DiameterReading1`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.DiameterReading2`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.DiameterReading3`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.DiameterReading4`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.SoilHeightLimit`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.TopOfCollarToSoilLowerLimit`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.Equipment.Moulds.Moulds.MouldAndCollarHeight`]: (configArgs: IConfigFunctionArgs) =>
        getReadabilityBasedMouldTypeValue(configArgs.worksheet, prefix),
      [`${prefix}.TestPortions.WaterAddedTargetPercent`]: { format: { nearest: 0 } },
      [`${prefix}.TestPortions.WaterAddedPercent`]: { format: { nearest: 0.1 } },
      [`${prefix}.TestPortions.Curing.Duration`]: { uom: 'h', format: { nearest: 0.1 } },
      [`${prefix}.TestPortions.WetDensity`]: { uom: 'Mg/m³', format: { nearest: 0.01 } },
      [`${prefix}.TestPortions.DryDensity`]: { uom: 'Mg/m³', format: { nearest: 0.01 } },

      [`${prefix}.TestFractionParticleDensity.ParticleDensity`]: { uom: 'Mg/m³', format: { nearest: 0.01 } },
      [`${prefix}.TestFractionParticleDensity.AssociatedTestFractionParticleDensity.ExternalTest.Results.ParticleDensity`]:
        {
          uom: 'Mg/m³',
          format: { nearest: 0.01 },
        },

      [`${prefix}.Results.MaximumDryDensity`]: { uom: 'Mg/m³', format: { nearest: 0.01 } },
      [`${prefix}.Results.OptimumMoistureContent`]: { format: { type: FormatTypes.SignificantFigures, precision: 2 } },

      ...getConfigForTest(
        qestIds.moistureContent.BS_EN_ISO_17892_1.rev2014,
        `${prefix}.TestPortions.MoistureDetermination`
      ),
      ...getConfigForMeasuredTest(
        qestIds.oversizeDetermination.BS_1377_2_Clause_11,
        `${prefix}.Preparation.AssociatedOversizeDetermination`
      ),
      ...getConfigForImportedTest(
        qestIds.psdSieveAnalysis.BS_EN_ISO_17892_4_Clause_5_2.Rev2016,
        `${prefix}.Preparation.AssociatedOversizeDetermination`
      ),
      [`${prefix}.Preparation.OversizeDeterminationResults.Sieves.CumulativeDryMassPassingPercent`]: {
        format: { nearest: 1 },
      },
      [`${prefix}.Preparation.OversizeDeterminationResults.Sieves.CumulativeDryMassRetainedPercent`]: {
        format: { nearest: 1 },
      },
      [`${prefix}.Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves.CumulativeDryMassRetainedPercent`]:
        {
          format: { nearest: 1 },
        },
      [`${prefix}.Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves.CumulativeDryMassRetainedPercent`]:
        {
          format: { nearest: 1 },
        },

      [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
      [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },
    },
    getLabelConfig(`${prefix}`),
    getMeasuredUsingConfig(`${prefix}`),
    getDerivedFromMeasurementsConfig(`${prefix}`),
    getDerivedFromMeasurementsConfigBS(`${prefix}`),
    {
      [`${prefix}.Equipment.CompactionBalance`]: { defaultReadability: 1, defaultUom: 'g' },
      [`${prefix}.Equipment.TareMassBalance`]: { defaultReadability: 1, defaultUom: 'g' },
      [`${prefix}.Equipment.MaterialRetainedBalance`]: { defaultReadability: 1, defaultUom: 'g' },
    }
  );

export default getConfig;
